import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PiggyBank, Clock, CheckCircle2, ArrowDownToLine, RotateCcw, Settings } from "lucide-react";
import { toast } from "sonner";
import { usePricing, finalCashbackRate, cad } from "@/lib/pricing";

export default function AdminCashback() {
  const cashbackRate = finalCashbackRate(usePricing());
  const [overview, setOverview] = useState(null);
  const [config, setConfig] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/cashback/overview").then((r) => setOverview(r.data)),
    api.get("/admin/cashback/config").then((r) => setConfig(r.data)),
    api.get("/admin/wallet-payouts?status=requested").then((r) => setPayouts(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const saveCfg = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/cashback/config", {
        holding_period_days: Number(config.holding_period_days),
        min_withdrawal: Number(config.min_withdrawal),
      });
      toast.success("Cashback config saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed (super admin only)");
    } finally { setBusy(false); }
  };

  const settle = async (id, action) => {
    try {
      await api.post(`/admin/wallet-payouts/${id}/settle?action=${action}`);
      toast.success(`Payout ${action}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${action} failed`);
    }
  };

  if (!overview || !config) return <div className="text-slate-500 text-sm">Loading…</div>;
  const t = overview.totals;

  const tiles = [
    { key: "total_cashback_lifetime", label: "Total lifetime", icon: PiggyBank, color: "pf-gradient-text-2" },
    { key: "pending", label: `Pending (${t.pending_count})`, icon: Clock, color: "text-amber-600" },
    { key: "approved", label: `Approved (${t.approved_count})`, icon: CheckCircle2, color: "text-emerald-600" },
    { key: "withdrawn", label: `Withdrawn (${t.withdrawn_count})`, icon: ArrowDownToLine, color: "text-cyan-700" },
    { key: "refunded", label: `Refunded (${t.refunded_count})`, icon: RotateCcw, color: "text-red-600" },
  ];

  return (
    <div data-testid="admin-cashback">
      <div className="pf-chip pf-chip-mint"><PiggyBank className="w-3 h-3" /> Cashback governance</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Cashback overview</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl" data-testid="admin-cashback-rate-copy">Platform-wide cashback health. Rules: {cashbackRate ? `${cashbackRate} standard rate on every verified purchase (2026_10_FINAL)` : <>0.75% ≤ CA$200, 1% &gt; CA$200</>} — automatic after every verified purchase.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
        {tiles.map((tile) => (
          <div key={tile.key} className="pf-card p-5" data-testid={`acb-kpi-${tile.key}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
              <tile.icon className="w-3 h-3" /> {tile.label}
            </div>
            <div className={`font-display text-2xl font-medium mt-2 tabular-nums ${tile.color}`}>
              {cad((t[tile.key] || 0))}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={saveCfg} className="pf-card p-6 mt-6 max-w-xl" data-testid="acb-config">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1 mb-4">
          <Settings className="w-3 h-3" /> Platform config (super admin only)
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Holding period (days)</label>
            <input type="number" min="0" max="90" value={config.holding_period_days}
                   onChange={(e) => setConfig({ ...config, holding_period_days: e.target.value })}
                   className="pf-input w-full px-3 py-2 text-sm" data-testid="acb-cfg-days" />
            <p className="text-[10px] text-slate-500 mt-1">Days from credit before pending → approved.</p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Min withdrawal (CAD)</label>
            <input type="number" step="0.01" min="0" value={config.min_withdrawal}
                   onChange={(e) => setConfig({ ...config, min_withdrawal: e.target.value })}
                   className="pf-input w-full px-3 py-2 text-sm" data-testid="acb-cfg-min" />
            <p className="text-[10px] text-slate-500 mt-1">Minimum shopper cashback balance to withdraw.</p>
          </div>
        </div>
        <div className="mt-4"><button disabled={busy} type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="acb-cfg-save">Save config</button></div>
      </form>

      <div className="pf-card p-5 mt-6" data-testid="acb-payouts">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Pending withdrawal requests</div>
          <span className="pf-chip">{payouts.length} pending</span>
        </div>
        {payouts.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No pending payouts.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500"><tr><th className="pb-2">Shopper</th><th className="pb-2">Amount</th><th className="pb-2">Requested</th><th className="pb-2"></th></tr></thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-t border-white/60" data-testid={`acb-payout-${p.id}`}>
                  <td className="py-2">{p.user_email}</td>
                  <td className="py-2 font-mono">{cad(p.amount)}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="py-2 text-right space-x-2">
                    <button onClick={() => settle(p.id, "paid")} className="pf-btn-primary px-3 py-1 rounded-full text-xs font-semibold" data-testid={`acb-pay-${p.id}`}>Mark paid</button>
                    <button onClick={() => settle(p.id, "rejected")} className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold text-red-700" data-testid={`acb-reject-${p.id}`}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
